import { Alert, Divider, Loader, MultiSelect, Select, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { MONO, QUEUE_COLOR, SANS } from './queueTheme'

// Sentinel Select value for "generate no care tasks for this org". Kept distinct
// from an empty string so it's an explicit, selectable option (never the
// placeholder) and never collides with a real board id.
const OFF = '__off__'

// Shared field chrome, matching the board: a mono uppercase label above the
// control and the explanation BELOW it. The description used to sit above the
// label, so every setting made you read a paragraph before learning what it
// configured. Mantine's own `styles`/`inputWrapperOrder` do this rather than
// Tailwind classes, since its input CSS is unlayered and outranks utilities.
const FIELD_ORDER = ['label', 'input', 'description', 'error'] as const

const fieldStyles = {
  label: {
    fontFamily: MONO,
    fontSize: 10.5,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: QUEUE_COLOR.muted,
    marginBottom: 6,
  },
  description: { fontSize: 12.5, lineHeight: 1.5, color: QUEUE_COLOR.subtle, marginTop: 6 },
}

const SectionHeading = ({ title, note }: { title: string; note?: string }) => (
  <div className="flex flex-col gap-1">
    <div className="text-[13.5px] font-semibold" style={{ color: QUEUE_COLOR.ink }}>
      {title}
    </div>
    {note ? (
      <div className="text-[12.5px]" style={{ color: QUEUE_COLOR.subtle }}>
        {note}
      </div>
    ) : null}
  </div>
)

// Org-scoped care task automation: which boards AI follow-ups and lab follow-ups
// land on, and who they are auto-assigned to. The endpoint resolves the
// caller's own org server-side (same scoping as the board list), so this shows
// and edits only the logged-in user's organization — there is no org picker.
export function CareTaskSettingsModal() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const configKey = orpc.careTaskConfigGet.queryKey({ input: {} })

  const { data, isLoading, isError } = useQuery(orpc.careTaskConfigGet.queryOptions({ input: {} }))

  const setConfig = useMutation(
    orpc.careTaskConfigSet.mutationOptions({
      // Optimistic so the controls settle on the chosen value immediately; reconcile
      // with the server on settle (same queryKey-not-key() caveat as the board hooks).
      // Only the fields PRESENT in `vars` are patched, matching the server's
      // preserve-on-omit semantics — each control sends just the field it owns, so
      // neither can revert the other by echoing back a stale value.
      onMutate: async (vars: {
        boardId?: string | null
        labBoardId?: string | null
        letterBoardId?: string | null
        identityBoardId?: string | null
        assignees?: string[]
      }) => {
        await queryClient.cancelQueries({ queryKey: configKey })
        const previous = queryClient.getQueryData<typeof data>(configKey)
        if (previous) {
          queryClient.setQueryData(configKey, {
            ...previous,
            ...(vars.boardId !== undefined ? { boardId: vars.boardId } : {}),
            ...(vars.labBoardId !== undefined ? { labBoardId: vars.labBoardId } : {}),
            ...(vars.letterBoardId !== undefined ? { letterBoardId: vars.letterBoardId } : {}),
            ...(vars.identityBoardId !== undefined
              ? { identityBoardId: vars.identityBoardId }
              : {}),
            ...(vars.assignees !== undefined ? { assignees: vars.assignees } : {}),
          })
        }
        return { previous }
      },
      onError: (error, _vars, ctx) => {
        if (ctx?.previous) queryClient.setQueryData(configKey, ctx.previous)
        showNotification({
          color: 'red',
          title: 'Failed to update care task settings',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: configKey }),
    }),
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader size="sm" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Alert color="red" variant="light">
        Couldn’t load care task settings. Please close and try again.
      </Alert>
    )
  }

  const boardOptions = [
    { value: OFF, label: 'None (off)' },
    ...data.boards.map((b) => ({ value: b.id, label: b.name })),
  ]

  // Dropdown options, alphabetical by name.
  const candidateOptions = data.candidates
    .map((c) => ({ value: c.reference, label: c.display }))
    .sort((a, b) => a.label.localeCompare(b.label))
  // Order the selected pills alphabetically too (display-only — the stored order
  // is irrelevant to the assigner). Falls back to the raw reference for a pick
  // whose candidate is no longer listed.
  const labelOf = new Map(data.candidates.map((c) => [c.reference, c.display]))
  const selectedAssignees = [...data.assignees].sort((a, b) =>
    (labelOf.get(a) ?? a).localeCompare(labelOf.get(b) ?? b),
  )
  const generationOff =
    data.boardId === null && data.labBoardId === null && data.identityBoardId === null

  const handleBoardChange = (value: string | null) => {
    if (!value) return
    const boardId = value === OFF ? null : value
    if (boardId === data.boardId) return
    // Board-only change: omit `assignees` so the server preserves the allowlist.
    setConfig.mutate({ boardId })
  }

  const handleLabBoardChange = (value: string | null) => {
    if (!value) return
    const labBoardId = value === OFF ? null : value
    if (labBoardId === data.labBoardId) return
    // Lab-board-only change: omit the other fields so the server preserves them.
    setConfig.mutate({ labBoardId })
  }

  const handleLetterBoardChange = (value: string | null) => {
    if (!value) return
    const letterBoardId = value === OFF ? null : value
    if (letterBoardId === data.letterBoardId) return
    // Letters-board-only change: omit the other fields so the server preserves them.
    setConfig.mutate({ letterBoardId })
  }

  const handleIdentityBoardChange = (value: string | null) => {
    if (!value) return
    const identityBoardId = value === OFF ? null : value
    if (identityBoardId === data.identityBoardId) return
    // Identity-board-only change: omit the other fields so the server preserves them.
    setConfig.mutate({ identityBoardId })
  }

  const handleAssigneesChange = (assignees: string[]) => {
    // Send only the allowlist — omitting boardId makes the server keep whatever
    // board is stored. Echoing `data.boardId` back here could revert a board
    // change that hadn't settled into the query cache yet.
    setConfig.mutate({ assignees })
  }

  return (
    <Stack gap="lg" style={{ fontFamily: SANS }}>
      <Stack gap="sm">
        <SectionHeading
          title="Where new tasks land"
          note="Pick “None (off)” to stop generating that kind of task for this organization."
        />
        <Select
          label="Target board"
          description="An AI follow-up task, created when a progress note is submitted."
          inputWrapperOrder={[...FIELD_ORDER]}
          styles={fieldStyles}
          data={boardOptions}
          value={data.boardId ?? OFF}
          onChange={handleBoardChange}
          disabled={setConfig.isPending}
          rightSection={setConfig.isPending ? <Loader size="xs" /> : undefined}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Lab results board"
          description="A follow-up task, created when a lab fax is triaged to a patient — who is also emailed an invitation to schedule."
          inputWrapperOrder={[...FIELD_ORDER]}
          styles={fieldStyles}
          data={boardOptions}
          value={data.labBoardId ?? OFF}
          onChange={handleLabBoardChange}
          disabled={setConfig.isPending}
          rightSection={setConfig.isPending ? <Loader size="xs" /> : undefined}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Letter signature board"
          description="A “review and sign” task, created when a coordinator asks a provider to sign a letter — assigned to that provider, and completed when they sign it."
          inputWrapperOrder={[...FIELD_ORDER]}
          styles={fieldStyles}
          data={boardOptions}
          value={data.letterBoardId ?? OFF}
          onChange={handleLetterBoardChange}
          disabled={setConfig.isPending}
          rightSection={setConfig.isPending ? <Loader size="xs" /> : undefined}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Identity verification board"
          description="A “verify identity” task, created when a patient's online verification needs review or they ask to verify with our team — completed when they are verified."
          inputWrapperOrder={[...FIELD_ORDER]}
          styles={fieldStyles}
          data={boardOptions}
          value={data.identityBoardId ?? OFF}
          onChange={handleIdentityBoardChange}
          disabled={setConfig.isPending}
          rightSection={setConfig.isPending ? <Loader size="xs" /> : undefined}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      </Stack>

      <Divider color={QUEUE_COLOR.hairline} />

      <Stack gap="sm">
        <SectionHeading
          title="Auto-assignment"
          note="Applies to AI follow-ups, lab result follow-ups, identity verification tasks, and fax action tasks left unassigned."
        />
        <MultiSelect
          label="Auto-assign to"
          description="Shared out by open workload. Only the coordinators listed here receive tasks; leave empty to create them unassigned."
          inputWrapperOrder={[...FIELD_ORDER]}
          placeholder={candidateOptions.length ? 'Select care coordinators' : undefined}
          data={candidateOptions}
          value={selectedAssignees}
          onChange={handleAssigneesChange}
          disabled={setConfig.isPending || generationOff || candidateOptions.length === 0}
          searchable
          nothingFoundMessage="No care coordinators found"
          comboboxProps={{ withinPortal: true }}
          // Stack the selected pills one per line — a long allowlist wrapping
          // inline is hard to scan. The `pill` override is required, not
          // cosmetic: Mantine's pill carries `flex: 0` (basis 0%), which in a
          // column list stretches each plate to the full row width and leaves a
          // long empty tail past the remove button. `0 0 auto` restores the
          // natural hug-the-name width, so the × sits at the plate's end.
          styles={{
            ...fieldStyles,
            pillsList: { flexDirection: 'column', alignItems: 'flex-start' },
            pill: { flex: '0 0 auto' },
          }}
        />
        {generationOff && (
          <Text size="xs" c="dimmed">
            Turn on a target board to enable auto-assignment.
          </Text>
        )}
        {!generationOff && candidateOptions.length === 0 && (
          <Text size="xs" c="dimmed">
            No care coordinators are set up for this organization yet.
          </Text>
        )}
      </Stack>
    </Stack>
  )
}
